import React, { memo, forwardRef, useCallback } from 'react';
import { useGanttCalculations } from '@/hooks/useGanttCalculations';

interface AppelOffre {
  id: string;
  titre: string;
  reference: string;
  date_limite: string;
  date_publication?: string;
  date_debut_redaction_ao?: string;
  date_qr_soumissionnaires?: string;
  date_reponses_qr?: string;
  date_analyse_offres?: string;
  date_debut_soutenance?: string;
  date_fin_soutenance?: string;
  date_adjudication?: string;
  date_contractualisation?: string;
  interlocuteur_da?: string;
}

interface GanttTask {
  id: string;
  title: string;
  reference: string;
  startDate: Date;
  endDate: Date;
  type: 'redaction' | 'publication' | 'deadline' | 'qr' | 'analyse' | 'soutenance' | 'adjudication' | 'contractualisation';
  status: 'expired' | 'urgent' | 'upcoming';
  originalAO: AppelOffre;
}

interface TimelineColumn {
  date: Date;
  label: string;
  fullLabel: string;
  type: 'day' | 'week' | 'month';
}

interface GanttRowProps {
  aoId: string;
  ao: AppelOffre;
  tasks: GanttTask[];
  viewStartDate: Date;
  viewEndDate: Date;
  viewMode: 'monthly' | 'weekly' | 'daily';
  columnWidth: number;
  timelineWidth: number;
  timelineColumns: TimelineColumn[];
  onAOClick: (aoId: string) => void;
  onScroll?: (scrollLeft: number) => void;
}

export const GanttRow = memo(forwardRef<HTMLDivElement, GanttRowProps>(
  ({ 
    aoId, 
    ao, 
    tasks, 
    viewStartDate, 
    viewEndDate, 
    viewMode, 
    columnWidth, 
    timelineWidth,
    timelineColumns,
    onAOClick,
    onScroll 
  }, ref) => {
    const { getTaskPosition, getTaskColor, getTaskTooltip } = useGanttCalculations(
      viewStartDate,
      viewEndDate,
      viewMode,
      columnWidth,
      timelineColumns
    );

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
      onScroll?.(e.currentTarget.scrollLeft);
    }, [onScroll]);

    const handleAOClick = useCallback(() => {
      onAOClick(aoId);
    }, [aoId, onAOClick]);

    return (
      <div className="flex items-center hover:bg-muted/30 p-2 rounded-lg transition-smooth">
        <div className="w-64 pr-4 flex-shrink-0">
          <div className="cursor-pointer" onClick={handleAOClick}>
            <div className="font-medium text-sm text-primary hover:underline truncate">
              {ao.titre}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {ao.reference}
            </div>
          </div>
        </div>
        
        <div 
          ref={ref}
          className="flex-1 relative h-8 overflow-x-auto scrollbar-thin"
          onScroll={handleScroll}
        >
          {/* Background grid */}
          <div 
            className="absolute inset-0 flex"
            style={{ width: timelineWidth }}
          >
            {Array.from({ length: Math.ceil(timelineWidth / columnWidth) }).map((_, index) => (
              <div 
                key={index} 
                className="border-r border-border/20 last:border-r-0 h-full flex-shrink-0"
                style={{ width: columnWidth }}
              />
            ))}
          </div>
          
          {/* Task bars */}
          {tasks.map((task) => {
            const position = getTaskPosition(task);
            
            if (!position.isVisible) return null;
            
            return (
              <div
                key={task.id}
                className={`absolute h-6 rounded-sm cursor-pointer transition-all hover:opacity-80 ${getTaskColor(task)} top-1`}
                style={{
                  left: `${position.left}px`,
                  width: `${position.width}px`,
                }}
                onClick={handleAOClick}
                title={getTaskTooltip(task)}
              >
                {viewMode !== 'daily' && position.width > 40 && (
                  <div className="text-xs text-white p-1 truncate">
                    {task.type === 'redaction' ? 'Réd.' : 
                     task.type === 'publication' ? 'Pub.' :
                     task.type === 'qr' ? 'Q&R' :
                     task.type === 'analyse' ? 'Analyse' :
                     task.type === 'soutenance' ? 'Sout.' :
                     task.type === 'adjudication' ? 'Adj.' :
                     task.type === 'contractualisation' ? 'Contr.' : ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
));

GanttRow.displayName = 'GanttRow';